
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { X, Search, User, Users } from 'lucide-react';

interface NewChatModalProps {
    onClose: () => void;
    onSuccess: (conversationId: string) => void;
}

const NewChatModal: React.FC<NewChatModalProps> = ({ onClose, onSuccess }) => {
    const { user } = useAuth();
    const [mode, setMode] = useState<'dm' | 'group'>('dm');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
    const [groupName, setGroupName] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (searchQuery.trim().length >= 2) {
            searchUsers();
        } else {
            setSearchResults([]);
        }
    }, [searchQuery]);

    const searchUsers = async () => {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('id, name, avatar, role, unit_type')
                .ilike('name', `%${searchQuery}%`)
                .neq('id', user?.id) // Don't show self
                .eq('is_active', true)
                .limit(10);

            if (error) throw error;
            setSearchResults(data || []);
        } catch (error) {
            console.error('Error searching users:', error);
        }
    };

    const handleUserSelect = (selectedUser: any) => {
        if (mode === 'dm') {
            startDirectMessage(selectedUser);
        } else {
            // Group mode: toggle selection
            if (selectedUsers.find(u => u.id === selectedUser.id)) {
                setSelectedUsers(prev => prev.filter(u => u.id !== selectedUser.id));
            } else {
                setSelectedUsers(prev => [...prev, selectedUser]);
            }
        }
    };

    const startDirectMessage = async (targetUser: any) => {
        setLoading(true);
        try {
            // Check if DM already exists
            // This is complex in pure SQL without a helper function, but we can do a quick check via participants
            // For now, let's just create a new one or handle duplication logic on backend usually.
            // Simplified approach: just create a new one every time or rely on a "find existing" RPC.
            // Let's create a new conversation for simplicity of this implementation step.

            const { data: conv, error: convError } = await supabase
                .from('chat_conversations')
                .insert({ type: 'dm', created_by: user?.id })
                .select()
                .single();

            if (convError) throw convError;

            // Add self first (to satisfy RLS "exists" check for subsequent adds)
            const { error: selfError } = await supabase
                .from('chat_participants')
                .insert({ conversation_id: conv.id, user_id: user?.id });

            if (selfError) throw selfError;

            // Add other participant
            const { error: partError } = await supabase
                .from('chat_participants')
                .insert({ conversation_id: conv.id, user_id: targetUser.id });

            if (partError) throw partError;

            onSuccess(conv.id);
        } catch (error) {
            console.error('Error starting DM:', error);
            alert('Sohbet başlatılamadı.');
        } finally {
            setLoading(false);
        }
    };

    const createGroup = async () => {
        if (!groupName.trim() || selectedUsers.length === 0) return;
        setLoading(true);

        try {
            const { data: conv, error: convError } = await supabase
                .from('chat_conversations')
                .insert({
                    type: 'group',
                    name: groupName,
                    created_by: user?.id
                })
                .select()
                .single();

            if (convError) throw convError;

            // Add self first
            const { error: selfError } = await supabase
                .from('chat_participants')
                .insert({ conversation_id: conv.id, user_id: user?.id });

            if (selfError) throw selfError;

            // Add other participants
            const others = selectedUsers.map(u => ({ conversation_id: conv.id, user_id: u.id }));
            if (others.length > 0) {
                const { error: partError } = await supabase
                    .from('chat_participants')
                    .insert(others);

                if (partError) throw partError;
            }

            onSuccess(conv.id);
        } catch (error) {
            console.error('Error creating group:', error);
            alert('Grup oluşturulamadı.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">Yeni Sohbet</h2>
                    <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="p-4">
                    <div className="flex space-x-2 mb-4">
                        <button
                            onClick={() => setMode('dm')}
                            className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${mode === 'dm'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                                }`}
                        >
                            Kişi Seç
                        </button>
                        <button
                            onClick={() => setMode('group')}
                            className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${mode === 'group'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                                }`}
                        >
                            Grup Oluştur
                        </button>
                    </div>

                    {mode === 'group' && (
                        <div className="mb-4">
                            <input
                                type="text"
                                placeholder="Grup Adı"
                                value={groupName}
                                onChange={(e) => setGroupName(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    )}

                    <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Kullanıcı ara..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="max-h-60 overflow-y-auto space-y-2">
                        {searchResults.map(result => (
                            <div
                                key={result.id}
                                onClick={() => handleUserSelect(result)}
                                className={`flex items-center space-x-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${selectedUsers.find(u => u.id === result.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                                    }`}
                            >
                                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                                    <span className="font-bold text-gray-600 dark:text-gray-300">{result.name.charAt(0)}</span>
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium text-gray-900 dark:text-white">{result.name}</p>
                                    <p className="text-xs text-gray-500">{result.role} - {result.unit_type || 'Genel'}</p>
                                </div>
                                {mode === 'group' && selectedUsers.find(u => u.id === result.id) && (
                                    <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                                        <X className="w-3 h-3 text-white" />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {mode === 'group' && (
                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={createGroup}
                                disabled={!groupName || selectedUsers.length === 0 || loading}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Oluşturuluyor...' : `Oluştur (${selectedUsers.length} kişi)`}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NewChatModal;
